import React, { useEffect, useMemo } from 'react';
import { IPlanCode } from '../../../utils/Interfaces';
import {
  IReactivateMembershipContext,
  useReactivateMyMembershipContext,
} from '../../../utils/ContextProviders/ReactivateContext';
import useGetPricesByPlan from '../../../utils/hooks/useGetPricesByPlan';
import PriceSummary from './PriceSummary';
import usePriceCalculator from '../../../hooks/usePriceCalculator';
import useCouponSelector from '../../../hooks/useCouponSelector';
import { couponScreenKey } from '../../constants';
import useCouponContent from '../../../hooks/useCouponContent';

// This is use to calculate properly when the coupon code is for 100% discount
const SEASONAL_PRICE = 59.99;

const PriceSummaryContainer: React.FC = () => {
  const { selectedPlanCode, growthMyMembership, couponsApplied } =
    useReactivateMyMembershipContext();

  const { variables } = growthMyMembership as IReactivateMembershipContext;
  const { isLoadingPrices, seasonalPrice, annualPrice } =
    useGetPricesByPlan(growthMyMembership);

  const isAnnualPlan = selectedPlanCode === IPlanCode.ANNUAL;

  const planPrice = isAnnualPlan
    ? annualPrice?.annualPrice
    : seasonalPrice?.price;

  const { getCouponContentKey, handleCouponChange } = useCouponContent(
    couponScreenKey.REACTIVATE_EXPIRED_SUBSCRIPTION
  );

  const {
    handleApplyCoupon,
    handleRemoveCoupon,
    setCouponError,
    couponError,
    discount,
    couponCode,
  } = useCouponSelector(handleCouponChange);

  useEffect(() => {
    if (couponCode) {
      handleApplyCoupon(couponCode);
    }
  }, [couponCode]);

  const totalDiscount = useMemo(() => {
    if (
      discount > 0 &&
      discount === SEASONAL_PRICE &&
      growthMyMembership.enabled
    ) {
      if (growthMyMembership.enabled && !isAnnualPlan) {
        const discountValue = discount ? Number(discount) : 0;
        return (discountValue + variables?.increasedPrice).toFixed(2);
      }
    }
    return discount ? Number(discount) : 0;
  }, [
    discount,
    variables.increasedPrice,
    growthMyMembership,
    isAnnualPlan,
  ]) as number;

  const totalAmounts = usePriceCalculator(
    selectedPlanCode,
    planPrice,
    totalDiscount
  );

  const shippingCost = `$${totalAmounts.shipping.toFixed(2)}`;
  const taxCost = `$${totalAmounts.tax.toFixed(2)}`;
  const totalCost = `$${totalAmounts.total.toFixed(2)} USD`;

  return (
    <PriceSummary
      selectedPlanCode={selectedPlanCode}
      planPrice={`$${planPrice}`}
      isLoadingPrices={isLoadingPrices}
      taxCost={taxCost}
      shippingCost={shippingCost}
      totalPrice={totalCost}
      discount={totalDiscount}
      seasonMembershipName={variables.seasonMembershipName}
      couponCodeConfig={{
        handleApplyCoupon,
        handleRemoveCoupon,
        couponsApplied,
        couponError,
        discount,
        getCouponContentKey,
        initialCouponCode: couponCode,
        setCouponError,
      }}
    />
  );
};

export default PriceSummaryContainer;
